import type {AnimationEvent, ReactElement} from 'react';
import {useLayoutEffect, useRef, useState} from 'react';
import {TextSize} from '@Components/text';
import {getBrandKitDisplayName} from '@Libraries/brands-library';
import {getKitNameFontStyle} from '@Components/mystuff-brands/components/brand-kit-pill-row/brand-kit-pill-row.helper';
import {ClickableDiv} from '@Components/clickable-div';
import {noop} from '@Utils/general.util';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';
import {useBrandKitRename} from '../../use-brand-kit-rename';
import {BrandKitRenameField} from '../brand-kit-rename-field/brand-kit-rename-field';
import {BrandKitRenameControl} from '../brand-kit-rename-control/brand-kit-rename-control';
import type {BrandKitPillProps} from './brand-kit-pill.types';
import styles from './brand-kit-pill.module.scss';

/**
 * One kit in the row: its name plus a mini cluster of its colours, or the default kit's "Default" tag; renaming turns the name into an input and the cluster into a save tick.
 * While the save is in flight the pill shows the new name and a spinner in the cluster's place.
 */
export function BrandKitPill({
  brandKit,
  isActive,
  cluster,
  isDefaultTagShown = false,
  headingFontFamily,
  onSelect,
  onRename,
  isRenameSaving = false,
  renameRequestCount = 0,
  hasHoverPencil = false,
  isNameCompact = false,
  onRenamingChange,
  isEntering = false,
  onEnterAnimationEnd = noop,
}: BrandKitPillProps): ReactElement {
  const pillRef = useRef<HTMLDivElement>(null);
  const [hasQuietFocus, setHasQuietFocus] = useState(false);
  const rename = useBrandKitRename({
    savedName: getBrandKitDisplayName(brandKit),
    onRename,
    isRenameSaving,
    renameRequestCount,
    onRenamingChange,
    onRenameEndedFromKeyboard: (): void => {
      setHasQuietFocus(true);
    },
    rowRef: pillRef,
  });
  const restingWidthRef = useRef(0);
  const isTabletWidth = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.BRAND_KITS_TABLET_BREAKPOINT);

  /**
   * Enter or Escape ends a rename with focus gone down with the field, so the pill takes it back for the keyboard
   * to carry on from. Focus moved by a key press would draw the focus ring and the pencil around the pill the user
   * just finished with, so the pill holds that focus quietly until focus next moves. Focused before paint, with the
   * quiet look already applied, so the ring never shows for a frame.
   */
  useLayoutEffect(() => {
    if (hasQuietFocus) {
      pillRef.current?.focus({preventScroll: true});
    }
  }, [hasQuietFocus]);

  const endQuietFocus = (): void => {
    setHasQuietFocus(false);
  };

  /** Only the pill's own entrance counts; the cluster and rename controls animate inside it too. */
  const onPillAnimationEnd = (event: AnimationEvent<HTMLDivElement>): void => {
    if (event.target === event.currentTarget) {
      onEnterAnimationEnd();
    }
  };

  /**
   * On desktop the rename field and its check are laid out wider than the name and its swatches, and a pill
   * that grew would wrap the row it was fitted into. So the pill holds its resting width while renamed, and
   * the input takes the room the name had. Set before paint, so the wider pill is never seen. Tablets and
   * phones let the pill grow with what is typed instead: their rows take one pill or scroll sideways.
   */
  useLayoutEffect(() => {
    const pill = pillRef.current;

    if (!pill) {
      return;
    }

    if (!rename.isRenaming || isTabletWidth) {
      pill.style.width = '';
      restingWidthRef.current = pill.getBoundingClientRect().width;
      return;
    }

    pill.style.width = `${restingWidthRef.current}px`;
  });

  return (
    <ClickableDiv
      ref={pillRef}
      className={`${styles.pill} flexbox flex-items-center corner-radius-full ${isActive ? styles.activePill : ''} ${rename.isRenaming ? styles.renamingPill : ''} ${hasQuietFocus ? styles.quietFocusPill : ''} ${isEntering ? styles.enteringPill : ''}`}
      aria-pressed={isActive}
      aria-label={rename.displayName}
      onClick={rename.isRenaming ? noop : onSelect}
      onBlur={endQuietFocus}
      onAnimationEnd={onPillAnimationEnd}
    >
      <span className={`${styles.nameSlot} inline-flexbox ${isNameCompact ? styles.compactNameSlot : ''}`} style={getKitNameFontStyle(headingFontFamily)}>
        <BrandKitRenameField rename={rename} textSize={TextSize.LG} />
      </span>
      <BrandKitRenameControl rename={rename} isRenameSaving={isRenameSaving} cluster={cluster} isDefaultTagShown={isDefaultTagShown} hasHoverPencil={hasHoverPencil} />
    </ClickableDiv>
  );
}
